import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const glitchMessages = [
  'PAGE NOT FOUND',
  'P4G3 N0T F0UND',
  '████ ███ █████',
  'YOU SHOULDN\'T BE HERE',
  'SOMETHING WENT WRONG',
  'ERROR ERROR ERROR',
  '¿PUƎOℲ ⊥ON ƎפɐԀ',
  'W̷̢̛̛̛̛͔̗̼̲͔̠̩͉̰͉͔̯͈̩͔̥̣͕̰͈̤̬̖͙͎͙͉̼̮̼͍̘̤̯̖͍̙͈͚͖̻̥̖̪͖̲̲̙̯̗̦͚̮̯͈̦̫̳͙̝̩͓̞̱͚̝̜̩̙̤̗͔̯̦̥̟̤͍̲̗͔̳̼͖̟̫̫̯͍̬̙̰͕͎̯̼̫̗̥͎̮̟͔̙͓̪͚̪̜̖̫̼͓̗̭̙͎̝̰̬̯̭̞͉̣̰̩̫̳̹̫̬̘̫͍̠̙̟͖̳͖͙̣͚̪̯̹̘̗̹͓͕̜̩̜̠̼͍̦̗̩̫̜̟̭̲̤̱̩̪͖̳̩͕̭͇͖̻̙̤̤̬̯̦͔̩̠̯̱̣̜͖̭̞̬̮̰̤̟͔̩̦̳̘̯͎̙̩̭̪͍̞͓̭̤̠̯̰̝̫̰̩̙̞̬̳̟̜̤̮̪̯̯͓̯̤̫̱̜̭̟̥̬̩̞̫̙̬̰̝̱̩̙̞̰̭̟̭̤̫̙̙̮̫̫̯̯̙̯̬̳̰̱̞̮̫̞̙̪̯͔̩̯̮̰̪̞̙̬̰̰̫̰̪̙̪̯̱̱̝̮̰̫̙̫̯̰̰̫̰̰̰̯͎̙̱̯̫̯̰̯̰̯̰̯̰̯̰͍̝̞̱̩̞̩̬̫̭̟̰̯̰̪̯̞͎̙̯̰̯̙̙̯̰̱̰̯̰̞̰̯̯̯̪̰̯̰̪̯̯̪̯̫̯̰̫͍̙̯̯̰̯̰̯̰̰̯͔̩̯̮̰̰̯̰̯̰̯̯̰̪̱̙̰̯̯̰̯̯̪̯̯̫̰̯̰̰̯̰̰̯̰̰̯̰̯̰̯̰̯̰̰̯̰̰̰̰̰̯̰̰̯̯̯̯̯̯̯̯̯̯̯̰̰̰̰̰̰̰̰̰̰̯̯̯̯̯̯̯̯̯̯̰̰̰̰̰̰̰̰̰̰',
];

const creepyFacts = [
  'You\'ve been staring at this page for 0 seconds...',
  'The void stares back.',
  'This URL leads nowhere. Like everything.',
  'Error 404: Happiness not found.',
  'You are lost. We all are.',
  'The page you seek has been consumed.',
  'Nothing exists here. Nothing ever did.',
  'Turn back. There\'s nothing for you here.',
];

const NotFoundPage: React.FC = () => {
  const [glitchText, setGlitchText] = useState('404');
  const [message, setMessage] = useState(glitchMessages[0]);
  const [creepyFact, setCreepyFact] = useState(creepyFacts[0]);
  const [seconds, setSeconds] = useState(0);
  const [showEye, setShowEye] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  // Glitch effect for 404
  useEffect(() => {
    const glitchChars = '!@#$%^&*()_+-=[]{}|;:,.<>?/~`0123456789';
    const interval = setInterval(() => {
      if (Math.random() > 0.7) {
        const glitched = '404'
          .split('')
          .map((char) =>
            Math.random() > 0.5
              ? glitchChars[Math.floor(Math.random() * glitchChars.length)]
              : char
          )
          .join('');
        setGlitchText(glitched);
        setTimeout(() => setGlitchText('404'), 100);
      }
    }, 200);
    return () => clearInterval(interval);
  }, []);

  // Cycle through creepy messages
  useEffect(() => {
    const interval = setInterval(() => {
      setMessage(glitchMessages[Math.floor(Math.random() * glitchMessages.length)]);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // Timer for how long user has been on page
  useEffect(() => {
    const interval = setInterval(() => {
      setSeconds((s) => s + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Update creepy fact based on time
  useEffect(() => {
    if (seconds === 0) {
      setCreepyFact('You\'ve been staring at this page for 0 seconds...');
    } else if (seconds < 5) {
      setCreepyFact(`You've been here for ${seconds} second${seconds > 1 ? 's' : ''}...`);
    } else if (seconds < 10) {
      setCreepyFact('Why are you still here?');
    } else if (seconds < 20) {
      setCreepyFact('Seriously. Leave.');
    } else if (seconds < 30) {
      setCreepyFact('I\'m watching you.');
      setShowEye(true);
    } else if (seconds < 45) {
      setCreepyFact('You can\'t escape.');
    } else if (seconds < 60) {
      setCreepyFact('I know what you did.');
    } else {
      setCreepyFact(`${seconds} seconds of your life, wasted here.`);
    }
  }, [seconds]);

  // Track mouse for eye following effect
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePos({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Calculate eye pupil position
  const calculatePupilPosition = () => {
    const eyeCenter = { x: window.innerWidth / 2, y: window.innerHeight / 2 - 100 };
    const angle = Math.atan2(mousePos.y - eyeCenter.y, mousePos.x - eyeCenter.x);
    const distance = Math.min(
      Math.sqrt(Math.pow(mousePos.x - eyeCenter.x, 2) + Math.pow(mousePos.y - eyeCenter.y, 2)),
      15
    );
    return {
      x: Math.cos(angle) * distance,
      y: Math.sin(angle) * distance,
    };
  };

  const pupilPos = calculatePupilPosition();

  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-center relative overflow-hidden">
      {/* Scanlines overlay */}
      <div
        className="fixed inset-0 pointer-events-none z-10"
        style={{
          background:
            'repeating-linear-gradient(0deg, rgba(0,0,0,0.1) 0px, rgba(0,0,0,0.1) 1px, transparent 1px, transparent 2px)',
          animation: 'scanlines 0.1s linear infinite',
        }}
      />

      {/* Flickering background */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 0%, hsl(var(--background)) 70%)',
          animation: 'flicker 0.15s infinite',
        }}
      />

      {/* Creepy eye that watches you */}
      {showEye && (
        <div className="absolute top-20 right-10 md:right-20 animate-pulse">
          <div className="relative w-16 h-16 md:w-24 md:h-24 bg-white rounded-full flex items-center justify-center shadow-lg shadow-red-500/50">
            <div
              className="w-8 h-8 md:w-12 md:h-12 bg-red-600 rounded-full flex items-center justify-center transition-transform duration-100"
              style={{
                transform: `translate(${pupilPos.x}px, ${pupilPos.y}px)`,
              }}
            >
              <div className="w-4 h-4 md:w-6 md:h-6 bg-black rounded-full" />
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="relative z-20 text-center px-4">
        {/* Glitchy 404 */}
        <h1
          className="text-[8rem] md:text-[12rem] font-black leading-none tracking-tighter select-none"
          style={{
            textShadow: `
              2px 2px 0 hsl(var(--primary)),
              -2px -2px 0 hsl(var(--primary)),
              4px 0 0 rgba(255,0,0,0.5),
              -4px 0 0 rgba(0,255,255,0.5)
            `,
            animation: 'glitch 0.3s infinite',
          }}
        >
          {glitchText}
        </h1>

        {/* Cycling message */}
        <p
          className="text-xl md:text-3xl font-mono text-muted-foreground mt-4 h-12"
          style={{
            textShadow: '0 0 10px hsl(var(--primary) / 0.5)',
          }}
        >
          {message}
        </p>

        {/* Creepy fact */}
        <p className="text-sm md:text-base text-muted-foreground/70 mt-8 font-mono italic">
          {creepyFact}
        </p>

        {/* Escape button */}
        <div className="mt-12 relative group">
          <div className="absolute -inset-1 bg-gradient-to-r from-red-600 to-orange-600 rounded-lg blur opacity-25 group-hover:opacity-75 transition duration-1000 group-hover:duration-200" />
          <Link to="/">
            <Button
              size="lg"
              className="relative font-mono tracking-wider hover:animate-pulse"
            >
              <span className="mr-2">⚠</span>
              ESCAPE TO SAFETY
              <span className="ml-2">⚠</span>
            </Button>
          </Link>
        </div>

        {/* ASCII art */}
        <pre className="text-[0.4rem] md:text-xs text-muted-foreground/30 mt-16 font-mono whitespace-pre select-none">
{`
        ▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄
        █░░░░░░░░▀█▄▀▄▀██████░▀█▄▀▄▀██████░
        ░░░░░░░░░░░▀█▄█▄███▀░░░▀█▄█▄███░░░░
        ░░░░ YOU SHOULDN'T HAVE COME HERE ░░░░
        ░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
`}
        </pre>
      </div>

      {/* Inject keyframe animations */}
      <style>{`
        @keyframes glitch {
          0% { transform: translate(0); }
          20% { transform: translate(-2px, 2px); }
          40% { transform: translate(-2px, -2px); }
          60% { transform: translate(2px, 2px); }
          80% { transform: translate(2px, -2px); }
          100% { transform: translate(0); }
        }
        
        @keyframes flicker {
          0% { opacity: 0.97; }
          5% { opacity: 0.9; }
          10% { opacity: 0.97; }
          15% { opacity: 0.93; }
          20% { opacity: 0.97; }
          50% { opacity: 1; }
          80% { opacity: 0.95; }
          100% { opacity: 0.97; }
        }
        
        @keyframes scanlines {
          0% { transform: translateY(0); }
          100% { transform: translateY(2px); }
        }
      `}</style>
    </div>
  );
};

export default NotFoundPage;
